// Events and registrations (SPEC-v2 V1-V10; API.md 7.3, endpoints 54-61).
// The public forms are in ./public-forms.ts.
import { z } from "zod";
import { countryCode } from "../countries";
import {
  FIELD_MODES,
  GENDERS,
  QUESTION_KINDS,
  REGISTRATION_STATUSES,
  STAKEHOLDER_TYPES,
  type Gender,
  type InviteSkipReason,
  type RegistrationSource,
  type RegistrationStatus,
  type StakeholderType,
  type TorStatus,
} from "../enums";
import { LIMITS } from "../limits";
import {
  type ActorRef,
  checkDateOrder,
  email,
  emailOrEmpty,
  isoDate,
  optionalText,
  requiredText,
  searchQuery,
  uuid,
} from "./common";

// ---------- The form set-up ("RSVP builder") ----------

const fieldMode = z.enum(FIELD_MODES);

/** Which standard fields the registration form asks. Name and email are always asked. */
export const registrationFieldConfig = z
  .object({
    organisation: fieldMode,
    position: fieldMode,
    country: fieldMode,
    stakeholderType: fieldMode,
    accessNeeds: fieldMode,
    /** never required */
    gender: z.enum(["off", "optional"], {
      errorMap: () => ({ message: "Gender can be off or optional, never required." }),
    }),
  })
  .strict();
export type RegistrationFieldConfig = z.infer<typeof registrationFieldConfig>;

/** The set-up of a new event (API.md endpoint 55). */
export const DEFAULT_FORM_FIELDS: RegistrationFieldConfig = {
  organisation: "optional",
  position: "off",
  country: "off",
  stakeholderType: "off",
  accessNeeds: "off",
  gender: "off",
};

/** The standard fields in screen order, with their labels (the chooser and the public form). */
export const REGISTRATION_FIELD_LABELS: Record<keyof RegistrationFieldConfig, string> = {
  organisation: "Organisation",
  position: "Position",
  country: "Country",
  stakeholderType: "Type of organisation",
  accessNeeds: "Access or dietary needs",
  gender: "Gender",
};

export const questionSchema = z
  .object({
    /** uuid, generated by the client */
    id: uuid,
    label: requiredText(LIMITS.questionLabel, "Question"),
    kind: z.enum(QUESTION_KINDS),
    /** choice only: 2-10 items, each 1-100; [] otherwise */
    options: z.array(requiredText(LIMITS.questionOption, "Option")).default([]),
    required: z.boolean().default(false),
  })
  .strict()
  .superRefine((q, ctx) => {
    if (q.kind === "choice") {
      if (q.options.length < LIMITS.questionOptionsMin || q.options.length > LIMITS.questionOptionsMax) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["options"],
          message: `A list question needs ${LIMITS.questionOptionsMin} to ${LIMITS.questionOptionsMax} options.`,
        });
      }
      if (new Set(q.options).size !== q.options.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "Each option must be different." });
      }
    } else if (q.options.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "Only list questions have options." });
    }
  });
export type Question = z.output<typeof questionSchema>;
export type QuestionInput = z.input<typeof questionSchema>;

export const questionsSchema = z
  .array(questionSchema)
  .max(LIMITS.questionsMax, `At most ${LIMITS.questionsMax} extra questions.`)
  .superRefine((qs, ctx) => {
    if (new Set(qs.map((q) => q.id)).size !== qs.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Two questions have the same id." });
    }
  });

/** key = Question.id; text ≤ 1 000, choice = one of the options, yes/no = boolean */
export type Answers = Record<string, string | boolean>;

export const answersSchema = z.record(
  z.string().max(64),
  z.union([z.string().trim().max(LIMITS.answerText, "An answer can be at most 1,000 characters."), z.boolean()]),
);

/**
 * Checks answers against the event's current questions. Unknown keys are
 * dropped (answers to removed questions are not stored again); empty text
 * answers are dropped. Returns the clean answers and an error per question id.
 */
export function checkAnswers(
  questions: readonly Question[],
  answers: Answers,
): { answers: Answers; errors: Record<string, string> } {
  const clean: Answers = {};
  const errors: Record<string, string> = {};
  for (const q of questions) {
    const a = answers[q.id];
    const empty = a === undefined || (typeof a === "string" && a.trim() === "");
    if (empty) {
      if (q.required) errors[q.id] = "Answer this question.";
      continue;
    }
    if (q.kind === "yes_no") {
      if (typeof a !== "boolean") errors[q.id] = "Answer yes or no.";
      else clean[q.id] = a;
    } else if (q.kind === "choice") {
      if (typeof a !== "string" || !q.options.includes(a)) errors[q.id] = "Choose one of the options.";
      else clean[q.id] = a;
    } else if (typeof a !== "string") {
      errors[q.id] = "Type an answer.";
    } else if (a.length > LIMITS.answerText) {
      errors[q.id] = "An answer can be at most 1,000 characters.";
    } else {
      clean[q.id] = a.trim();
    }
  }
  return { answers: clean, errors };
}

// ---------- Requests ----------

const eventFields = {
  title: requiredText(LIMITS.eventTitle, "Title"),
  description: optionalText(LIMITS.eventDescription, "Description"),
  location: optionalText(LIMITS.location, "Location"),
  startDate: isoDate,
  endDate: isoDate,
  capacity: z
    .number({ invalid_type_error: "Enter a number." })
    .int("Use a whole number.")
    .min(1, "At least 1.")
    .max(LIMITS.capacityMax, `At most ${LIMITS.capacityMax}.`)
    .nullable(),
  contactEmail: emailOrEmpty,
  torId: uuid.nullable(),
};

/** 55. POST /api/events. `endDate` defaults to `startDate` (in the API). */
export const createEventBody = z
  .object({
    title: eventFields.title,
    description: eventFields.description.default(""),
    location: eventFields.location.default(""),
    startDate: eventFields.startDate,
    endDate: eventFields.endDate.optional(),
    capacity: eventFields.capacity.default(null),
    contactEmail: eventFields.contactEmail.default(""),
    torId: eventFields.torId.default(null),
  })
  .strict()
  .superRefine(checkDateOrder);
export type CreateEventBody = z.input<typeof createEventBody>;
export type CreateEventInput = z.output<typeof createEventBody>;

export const FORM_LINK_KEYS = ["registration", "checkIn", "feedback"] as const;
export type FormLinkKey = (typeof FORM_LINK_KEYS)[number];

/** 57. PATCH /api/events/:id */
export const updateEventBody = z
  .object({
    title: eventFields.title.optional(),
    description: eventFields.description.optional(),
    location: eventFields.location.optional(),
    startDate: eventFields.startDate.optional(),
    endDate: eventFields.endDate.optional(),
    capacity: eventFields.capacity.optional(),
    contactEmail: eventFields.contactEmail.optional(),
    torId: eventFields.torId.optional(),
    formFields: registrationFieldConfig.optional(),
    questions: questionsSchema.optional(),
    feedbackQuestions: questionsSchema.optional(),
    registrationOpen: z.boolean().optional(),
    checkInOpen: z.boolean().optional(),
    feedbackOpen: z.boolean().optional(),
    cancelled: z.boolean().optional(),
    rotateLinks: z
      .array(z.enum(FORM_LINK_KEYS))
      .min(1)
      .max(3)
      .refine((a) => new Set(a).size === a.length, "Name each link once.")
      .optional(),
  })
  .strict()
  .superRefine(checkDateOrder);
export type UpdateEventBody = z.input<typeof updateEventBody>;
export type UpdateEventInput = z.output<typeof updateEventBody>;

/** 54. GET /api/events?when&contractId&q */
export const eventListQuery = z
  .object({
    when: z.enum(["upcoming", "past", "all"]).default("upcoming"),
    contractId: uuid.optional(),
    q: searchQuery.optional(),
  })
  .strict();
export type EventListQuery = z.infer<typeof eventListQuery>;

/** The person fields of a registration (staff add and PATCH). */
const personFields = {
  name: requiredText(LIMITS.expertName, "Name"),
  email: email.nullable(),
  organisationName: optionalText(LIMITS.organisationName, "Organisation"),
  position: optionalText(LIMITS.positionTitle, "Position"),
  country: countryCode.nullable(),
  gender: z.enum(GENDERS).nullable(),
  stakeholderType: z.enum(STAKEHOLDER_TYPES).nullable(),
  accessNeeds: optionalText(LIMITS.accessNeeds, "Access or dietary needs"),
};

export const registrationPersonInput = z
  .object({
    name: personFields.name,
    email: personFields.email.default(null),
    organisationName: personFields.organisationName.default(""),
    position: personFields.position.default(""),
    country: personFields.country.default(null),
    gender: personFields.gender.default(null),
    stakeholderType: personFields.stakeholderType.default(null),
    accessNeeds: personFields.accessNeeds.default(""),
  })
  .strict();
export type RegistrationPersonInput = z.output<typeof registrationPersonInput>;

/**
 * 59. POST /api/events/:id/registrations. Two shapes, exactly one of them:
 * - invite contacts: `{ contactIds, sendInvitations }`
 * - add one person:  `{ person, checkIn }` (`checkIn: true` = walk-in, checked in)
 */
export const addRegistrationsBody = z
  .object({
    contactIds: z
      .array(uuid)
      .min(1, "Choose at least one contact.")
      .max(LIMITS.inviteContactsMax, `At most ${LIMITS.inviteContactsMax} contacts at a time.`)
      .optional(),
    sendInvitations: z.boolean().optional(),
    person: registrationPersonInput.optional(),
    checkIn: z.boolean().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const invite = v.contactIds !== undefined || v.sendInvitations !== undefined;
    const person = v.person !== undefined || v.checkIn !== undefined;
    if (invite === person) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [v.person === undefined ? "contactIds" : "person"],
        message: "Invite contacts, or add one person, not both.",
      });
    } else if (invite && v.contactIds === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["contactIds"], message: "Choose at least one contact." });
    } else if (person && v.person === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["person"], message: "Enter the person's details." });
    }
  });
export type AddRegistrationsBody = z.input<typeof addRegistrationsBody>;
export type AddRegistrationsInput = z.output<typeof addRegistrationsBody>;

/** 60. PATCH /api/registrations/:id. An anonymised registration only accepts `status` and `checkedIn`. */
export const updateRegistrationBody = z
  .object({
    name: personFields.name.optional(),
    email: personFields.email.optional(),
    organisationName: personFields.organisationName.optional(),
    position: personFields.position.optional(),
    country: personFields.country.optional(),
    gender: personFields.gender.optional(),
    stakeholderType: personFields.stakeholderType.optional(),
    accessNeeds: personFields.accessNeeds.optional(),
    status: z.enum(REGISTRATION_STATUSES).optional(),
    checkedIn: z.boolean().optional(),
  })
  .strict();
export type UpdateRegistrationBody = z.infer<typeof updateRegistrationBody>;

// ---------- Responses ----------

export type RegistrationCounts = {
  invited: number;
  registered: number;
  declined: number;
  cancelled: number;
  /** v3 statuses (always 0 for v2 events) */
  waitlisted: number;
  maybe: number;
  attended: number;
};

export type EventSummary = {
  id: string;
  title: string;
  location: string;
  startDate: string;
  endDate: string;
  capacity: number | null;
  tor: {
    id: string;
    title: string;
    activityCode: string;
    status: TorStatus;
    contractId: string;
    contractReference: string;
  } | null;
  counts: RegistrationCounts;
  registrationOpen: boolean;
  checkInOpen: boolean;
  feedbackOpen: boolean;
  cancelledAt: string | null;
  updatedAt: string;
};

export type Registration = {
  id: string;
  contactId: string | null;
  name: string;
  email: string | null;
  organisationName: string;
  position: string;
  country: string | null;
  gender: Gender | null;
  stakeholderType: StakeholderType | null;
  accessNeeds: string;
  answers: Answers;
  status: RegistrationStatus;
  source: RegistrationSource;
  consentAt: string | null;
  consentContact: boolean;
  invitedAt: string | null;
  invitationSentAt: string | null;
  checkedInAt: string | null;
  /** null while checkedInAt is set = self check-in */
  checkedInBy: ActorRef | null;
  anonymised: boolean;
  createdAt: string;
  updatedAt: string;
};

export type QuestionSummary =
  | { questionId: string; label: string; kind: "yes_no"; yes: number; no: number }
  | { questionId: string; label: string; kind: "choice"; options: { label: string; count: number }[] }
  /** answers newest first, ≤ 200 */
  | { questionId: string; label: string; kind: "text"; answers: string[] };

export type FeedbackSummary = {
  count: number;
  /** 1 decimal */
  overallAverage: number | null;
  /** answers scoring 1..5 */
  overallSpread: [number, number, number, number, number];
  /** current feedback questions, in order */
  questions: QuestionSummary[];
  /** non-empty, newest first, ≤ 200 */
  comments: { text: string; createdAt: string }[];
};

export type EventDetail = EventSummary & {
  description: string;
  contactEmail: string;
  formFields: RegistrationFieldConfig;
  questions: Question[];
  feedbackQuestions: Question[];
  /** full URLs: {APP_URL}/f/{token} */
  links: { registration: string; checkIn: string; feedback: string };
  /** by name; ≤ 5 000 */
  registrations: Registration[];
  feedback: FeedbackSummary;
  createdAt: string;
  can: {
    /** delete = editor and no registrations */
    edit: boolean;
    delete: boolean;
    openRegistration: boolean;
    /** e.g. "The linked ToR is not approved yet." */
    openRegistrationReason: string | null;
  };
};

/** 59. POST /api/events/:id/registrations */
export type AddRegistrationsResponse = {
  event: EventDetail;
  added: number;
  skipped: { contactId: string; reason: InviteSkipReason }[];
  emailed: number;
};

/** The public link of a form: {APP_URL}/f/{token} (the web route of the public forms). */
export const publicFormUrl = (appUrl: string, token: string): string => `${appUrl.replace(/\/+$/, "")}/f/${token}`;

